import { Box, IconButton, styled } from '@mui/material';
import Icon from 'DesignSystem/Primitives/Icon/Icon';
import ThemeTypography from 'DesignSystem/Primitives/Typography/ThemeTypography';
import React, { FC } from 'react';
import { Booleanish, Tbooleanish } from 'types/booleanish.types';
import { getCountryLabelFromCode, getCountryRiskLevelFromCode } from 'utils/country.utils';
import CountryRiskLevel from '../CountryRiskLevel/CountryRiskLevel';

interface IGeoDataItemProps {
  title: string;
  subtitle?: string;
  extraInfo?: string;
  status?: 'valid' | 'error' | 'warning' | 'default';
  commodity?: string;
  countryCode?: string;
  owner?: string;
  active?: boolean;
  checked?: boolean;
  disabled?: boolean;
  /**
   * If only sub set is selected
   */
  totalCount?: number;
  includedCount?: number;
  hasBoxShadow?: boolean;
  fullWidth?: boolean;
  amountFarms?: number;
  amountBatches?: number;
  weight?: number;
  onDeleteClick?: () => void;
  /**
   * If not passed the entity has not pointer and is not clickable
   */
  onClick?: () => void;
}

const Container = styled(Box)<{
  status?: 'error' | 'valid' | 'default' | 'warning';
  active?: Tbooleanish;
  disabled?: Tbooleanish;
  clickable?: Tbooleanish;
  'has-box-shadow'?: Tbooleanish;
  'full-width'?: Tbooleanish;
}>(
  ({
    theme,
    active,
    disabled,
    clickable,
    status = 'default',
    'has-box-shadow': hasBoxShadow,
    'full-width': fullWidth,
  }) => ({
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    padding: theme.spacing(1),
    paddingRight: theme.spacing(2),
    ...(clickable === 'true' && disabled !== 'true'
      ? { cursor: 'pointer' }
      : { cursor: 'default', pointerEvents: 'none' }),
    border:
      hasBoxShadow === 'true'
        ? 'none'
        : `1px solid ${
            status === 'error'
              ? theme.custom.themeColors.error[100]
              : status === 'warning'
              ? theme.custom.themeColors.accent[100]
              : theme.custom.colors.lightBorderColor
          }`,
    borderRadius: 4,
    background: theme.custom.themeColors.white,
    position: 'relative',
    width: fullWidth === 'true' ? '100%' : 'fit-content',
    height: 48,
    boxShadow: hasBoxShadow === 'true' ? theme.custom.shadows[1] : 'none',

    ':hover': {
      ...(active === 'false' && disabled === 'false'
        ? {
            backgroundColor: theme.custom.themeColors.grayScale[5],
            borderColor: theme.custom.themeColors.primary[40],
          }
        : {}),
    },

    '& .hover-btn': {
      visibility: 'hidden',
    },
    '&:hover .hover-btn': {
      visibility: 'visible',
    },

    ...(active === 'true' && {
      '&:before': {
        content: '""',
        display: 'block',
        width: 8,
        height: '100%',
        position: 'absolute',
        left: 0,
        top: 0,
        borderRadius: '4px 0 0 4px',
        backgroundColor:
          status === 'error'
            ? theme.custom.themeColors.error[100]
            : status === 'warning'
            ? theme.custom.themeColors.accent[100]
            : theme.custom.themeColors.primary[100],
        pointerEvents: 'none',
      },
      '&:after': {
        content: '""',
        display: 'block',
        height: '100%',
        position: 'absolute',
        left: 0,
        top: 0,
        width: '100%',
        border: `2px solid ${
          status === 'error'
            ? theme.custom.themeColors.error[100]
            : status === 'warning'
            ? theme.custom.themeColors.accent[100]
            : theme.custom.themeColors.primary[100]
        }`,
        borderRadius: 4,
        pointerEvents: 'none',
      },
    }),
  })
);

const TextContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
}));

const SubTextRow = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'row',
  alignItems: 'center',
  width: 'max-content',
}));

const SubText = styled(ThemeTypography)(({ theme }) => ({
  marginLeft: 2,
  fontSize: '10px !important',
}));

const SubItem = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'row',
  alignItems: 'center',
  marginRight: theme.spacing(1),
}));

const InfoContainer = styled(Box)(({ theme }) => ({
  backgroundColor: theme.custom.themeColors.primary[5],
  padding: theme.spacing(0.2, 0.75),
  borderRadius: 4,
}));

const GeoDataItem: FC<IGeoDataItemProps> = ({
  title,
  subtitle,
  extraInfo,
  status,
  commodity,
  countryCode,
  owner,
  active,
  checked,
  disabled,
  totalCount,
  includedCount,
  fullWidth,
  hasBoxShadow,
  amountFarms,
  amountBatches,
  weight,
  onDeleteClick,
  onClick,
}) => {
  const handleClickDelete = (e: React.MouseEvent) => {
    e.stopPropagation();
    onDeleteClick
      ? onDeleteClick()
      : console.warn('DatasetItem: onClickDelete is not defined for this item');
  };

  const countryRiskLevel = countryCode ? getCountryRiskLevelFromCode(countryCode) : undefined;

  return (
    <Container
      active={Booleanish(active)}
      disabled={Booleanish(disabled)}
      status={status}
      clickable={Booleanish(onClick)}
      has-box-shadow={Booleanish(hasBoxShadow)}
      full-width={Booleanish(fullWidth)}
      onClick={onClick}
    >
      <Box ml={1} />
      <Icon name="polygon-data" size="xx-large" color="almost-black" />
      <TextContainer ml={2}>
        <Box display="flex" alignItems="center" gap={1}>
          <Box display="flex" alignItems="center" gap={0.75}>
            <ThemeTypography variant="BODY_MEDIUM_BOLD" autoOverflow>
              {title}
            </ThemeTypography>
            {subtitle && (
              <ThemeTypography variant="BODY_SMALL" color="GRAY_60" autoOverflow>
                | {subtitle}
              </ThemeTypography>
            )}
            {extraInfo && (
              <ThemeTypography variant="BODY_SMALL" color="GRAY_60" autoOverflow>
                | {extraInfo}
              </ThemeTypography>
            )}
            {amountBatches !== undefined && (
              <InfoContainer>
                <ThemeTypography variant="BODY_SMALLER_BOLD" color="BLACK">
                  {amountBatches} batch{amountBatches !== 1 ? 'es' : ''}
                </ThemeTypography>
              </InfoContainer>
            )}
            {weight !== undefined && (
              <InfoContainer>
                <ThemeTypography variant="BODY_SMALLER_BOLD" color="BLACK">
                  {weight} kg
                </ThemeTypography>
              </InfoContainer>
            )}
            {totalCount !== undefined &&
              includedCount !== undefined &&
              includedCount < totalCount && (
                <ThemeTypography variant="BODY_SMALL_BOLD" color="GRAY_60">
                  ({includedCount}/{totalCount})
                </ThemeTypography>
              )}

            {countryRiskLevel && <CountryRiskLevel riskLevel={countryRiskLevel} />}

            {(status === 'valid' || checked) && <Icon name="check-filled" color="green" />}
            {status === 'error' && <Icon name="error-filled" color="red-80" />}
          </Box>

          {!!onDeleteClick && !disabled && (
            <IconButton className="hover-btn" size="small" onClick={handleClickDelete}>
              <Icon name="delete" size="small" />
            </IconButton>
          )}
        </Box>
        <SubTextRow>
          <SubItem>
            <Icon name="tag-filled" size="xx-small" />
            <SubText variant="LABEL_INPUT" color="GRAY_80" autoOverflow>
              Geo-dataset
            </SubText>
          </SubItem>

          {amountFarms !== undefined && (
            <SubItem>
              <Icon name="farm" size="small" />
              <SubText variant="LABEL_INPUT" color="GRAY_80" autoOverflow>
                {amountFarms} farm{amountFarms !== 1 ? 's' : ''}
              </SubText>
            </SubItem>
          )}
          {commodity && (
            <SubItem>
              <Icon name="raw-material" size="small" />
              <SubText variant="LABEL_INPUT" color="GRAY_80" autoOverflow>
                {commodity}
              </SubText>
            </SubItem>
          )}
          {countryCode && (
            <>
              <SubItem>
                <Icon name="activity-default" size="small" />
                <SubText variant="LABEL_INPUT" color="GRAY_80" autoOverflow>
                  {getCountryLabelFromCode(countryCode)}
                </SubText>
              </SubItem>
            </>
          )}
          {owner && (
            <SubItem>
              <Icon name="partner" size="small" />
              <SubText variant="LABEL_INPUT" color="GRAY_80">
                {owner}
              </SubText>
            </SubItem>
          )}
        </SubTextRow>
      </TextContainer>
    </Container>
  );
};

export default GeoDataItem;
